import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

import deleteWhiteIcon from "../../../assets/img/icons/delete_white.svg";
import goldenStarBadge from "../../../assets/img/icons/golden-star-badge.svg";
import PlusBlack from "../../../assets/img/icons/plus_black_2.svg";
import BackButtonComponent from "../../../components/BackButtonComponent/BackButtonComponent";
import ButtonTextLoader from "../../../components/Loaders/ButtonTextLoader";
import PricingPlan from "../../../components/PricingPlan";
import {
  ButtonIcon,
  DeleteButton,
  PrimaryButton,
  WhiteButton,
  WhiteButtonWithIcon,
} from "../../../styledComponents/buttons";
import {
  AddClientContainer,
  AddClientContainerNew,
  GeneralInfoItem,
  GeneralInfoSection,
  GeneralInfoSection2,
  HoveredSectionClient,
} from "../../../styledComponents/clients";
import {
  CardTitle,
  CommonFlex,
  CommonGrid,
  CommonImage,
  CommonText,
  Container,
  ContentHeader,
  FormAlert,
  InputField,
  InputLabel,
  InputWithLabelSection,
  ManagerBadge,
  PageTitle,
} from "../../../styledComponents/common";
import {
  ButtonFlexContainer,
  RedStar,
} from "../../../styledComponents/members";
import { checkHtml, checkUrl, validateEmail } from "../../../utils/helper";
import { DataAndMapContainer } from "../../FieldServices/JobSites/jobSitesStyles";
import { MapAndAlertContainer } from "../../FieldServices/RouteMap/routeMapStyles";
import AddLocationModal from "../ClientDetails/AddLocationModal";
import StaticTaskLocationModal from "../ClientDetails/StaticTaskLocationModal";

// import ClientMap from './ClientMap';

const AddClient = props => {
  const [clientName, setClientName] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [officeLocation, setOfficeLocation] = useState("");
  const [saveErrors, setSaveErrors] = useState({});
  const [save, setSave] = useState(false);
  const [pricingPopup, setPricingPopup] = useState(false);
  const [duplicateEmail, setDuplicateEmail] = useState(false);
  const [action, setAction] = useState("create");
  const [popupOpen, setPopupOpen] = useState(false);
  const [clientLocationList, setClientLocationList] = useState([]);
  const [staticLocationModalOpen, setStaticLocationModalOpen] = useState(false);
  const [selectedStaticLocation, setSelectedStaticLocation] = useState(null);
  const [locationIndex, setLocationIndex] = useState(null);
  const [hoveredId, setHoveredId] = useState(null);

  const customerInputRef = useRef(null);

  const { t } = useTranslation();
  const { createdClientId } = props;

  const clearStates = () => {
    setClientName("");
    setClientEmail("");
    setPhoneNumber("");
    setSaveErrors({});
  };

  useEffect(() => {
    customerInputRef.current.focus();
  }, []);

  useEffect(() => {
    if (props.duplicateEmail) {
      setDuplicateEmail(true);
    } else {
      setDuplicateEmail(false);
    }
  }, [props.duplicateEmail]);

  useEffect(() => {
    if (save && createdClientId !== null) {
      clearStates();
      props.history.push(`/user/customer-details?id=${createdClientId}`);
    }
  }, [createdClientId]);

  useEffect(() => {
    if (
      props.selectedOrganization?.plan?.name === "Standard" ||
      props.selectedOrganization?.plan?.nick_name === "standard-free"
    ) {
      pricingPopupToggle();
    }
  }, [props.selectedOrganization]);

  const onClientNameChange = e => {
    const { value } = e.target;
    setClientName(value);
    if (saveErrors && saveErrors["clientName"]) {
      delete saveErrors["clientName"];
    }
  };

  const onEmailChange = e => {
    setDuplicateEmail(false);
    const { value } = e.target;
    setClientEmail(value);
    if (saveErrors && saveErrors["clientEmail"]) {
      delete saveErrors["clientEmail"];
    }
  };

  const onAddressChange = e => {
    const { value } = e.target;
    setOfficeLocation(value);
  };

  const cancelFunction = () => {
    props.history.push({
      pathname: "/user/customers",
      state: { from: "create" },
    });
  };

  const pricingPopupToggle = () => {
    setPricingPopup(!pricingPopup);
  };

  const handleSave = () => {
    if (checkError()) {
      if (props.selectedOrganization && props.selectedOrganization.id) {
        let payload = {
          organization_id: props.selectedOrganization.id,
          name: clientName,
          showToast: false,
          phone_number: phoneNumber
            ? phoneNumber.startsWith("+")
              ? phoneNumber
              : "+" + phoneNumber
            : null,
          email: clientEmail ? clientEmail : null,
          location_list: clientLocationList,
        };
        props.createClient(payload);
        setSave(true);
      }
    }
  };

  const checkError = () => {
    let saveErrors = {};
    let formIsValid = true;
    if (!clientName.trim()) {
      formIsValid = false;
      saveErrors["clientName"] = t("please_enter_customer_name");
    } else if (clientName.length > 256) {
      formIsValid = false;
      saveErrors["clientName"] = t(
        "please_enter_customer_name_within_256_characters",
      );
    } else if (checkUrl(clientName)) {
      formIsValid = false;
      saveErrors["clientName"] = t("client_name_can_not_contain_url");
    } else if (checkHtml(clientName)) {
      formIsValid = false;
      saveErrors["clientName"] = t("invalid_client_name");
    }
    if (!validateEmail(clientEmail)) {
      formIsValid = false;
      saveErrors["clientEmail"] = t("please_enter_a_valid_email_address");
    }
    setSaveErrors(saveErrors);
    return formIsValid;
  };

  const addLocationToggle = () => {
    setPopupOpen(!popupOpen);
    setAction("create");
  };

  const handleClientLocation = locationData => {
    // Add new location with temporary ID
    const newLocation = {
      ...locationData,
      tempId: Date.now() + Math.random(),
      default: locationData.default || false,
    };

    // If new location is default, remove default from all existing locations
    const updatedList = clientLocationList.map(loc => ({
      ...loc,
      default: newLocation.default ? false : loc.default,
    }));

    setClientLocationList([...updatedList, newLocation]);
    setPopupOpen(false);
  };

  const handleClientLocationDelete = index => {
    const updatedList = clientLocationList.filter((_, i) => i !== index);
    setClientLocationList(updatedList);
  };

  const onHoverChange = index => {
    setHoveredId(index);
  };

  const handleShowLocation = (location, index) => {
    // Ensure static modal receives an id; use tempId for newly added locations
    const locationWithId = {
      ...location,
      id: location.id || location.tempId,
    };
    setSelectedStaticLocation(locationWithId);
    setLocationIndex(index);

    setTimeout(() => {
      setStaticLocationModalOpen(true);
    }, 300);
  };

  return (
    <div className="content">
      <PricingPlan
        isOpen={pricingPopup}
        toggle={pricingPopupToggle}
        popupText="You don't have permission to this feature."
        popupTitle="Upgrade to Elite to access this feature"
        history={props.history}
        backRoute="/user/dashboard"
        onlyElite
      />
      {/* <PageTitle>{t("customers")}</PageTitle> */}
      <ContentHeader>
        <BackButtonComponent
          onClick={() => props.history.push("/user/customers")}
          subTitle="Back to All Customers"
        />
      </ContentHeader>
      {props.selectedOrganization?.id && (
        <AddLocationModal
          isOpen={popupOpen}
          toggle={addLocationToggle}
          addClientLocation={handleClientLocation}
          organization_id={props.selectedOrganization.id}
          selectedOrganization={props.selectedOrganization}
          action={action}
        />
      )}

      <Container marginTop="12px" style={{ padding: `30px` }}>
        <CardTitle>{t("add_customer_details")}</CardTitle>
        <DataAndMapContainer columns="30% 70%">
          <AddClientContainerNew>
            <InputWithLabelSection>
              <InputLabel>
                {t("customer_name")} <RedStar>*</RedStar>
              </InputLabel>
              <InputField
                type="text"
                placeholder={t("type_customer_name_here")}
                ref={customerInputRef}
                value={clientName}
                onChange={e => onClientNameChange(e)}
                error={
                  saveErrors &&
                  saveErrors["clientName"] &&
                  saveErrors["clientName"] !== undefined
                }
              />
              {saveErrors &&
                saveErrors["clientName"] &&
                saveErrors["clientName"] !== undefined && (
                  <FormAlert margin="0 0 0 5px">
                    {saveErrors["clientName"]}
                  </FormAlert>
                )}
            </InputWithLabelSection>

            <InputWithLabelSection>
              <InputLabel>
                {t("email_address")} <RedStar>*</RedStar>
              </InputLabel>
              <InputField
                type="text"
                placeholder={t("type_email_address_here")}
                value={clientEmail}
                onChange={e => onEmailChange(e)}
                error={
                  saveErrors &&
                  saveErrors["clientEmail"] &&
                  saveErrors["clientEmail"] !== undefined
                }
              />
              {saveErrors &&
                saveErrors["clientEmail"] &&
                saveErrors["clientEmail"] !== undefined && (
                  <FormAlert margin="0 0 0 5px">
                    {saveErrors["clientEmail"]}
                  </FormAlert>
                )}
              {duplicateEmail && (
                <FormAlert margin="0 0 0 5px">Email already in use.</FormAlert>
              )}
            </InputWithLabelSection>

            <InputWithLabelSection>
              <InputLabel>{t("contact_number")}</InputLabel>
              <PhoneInput
                enableSearch
                country={"us"}
                value={phoneNumber}
                onChange={phone => setPhoneNumber(phone)}
                inputClass="phone-number-input"
                buttonClass="phone-number-flag-dropdown"
              />
            </InputWithLabelSection>

            <ButtonFlexContainer>
              <WhiteButtonWithIcon onClick={() => addLocationToggle()}>
                <ButtonIcon src={PlusBlack} alt="" />
                {clientLocationList?.length >= 1
                  ? t("add_more_location")
                  : t("add_location")}
              </WhiteButtonWithIcon>
            </ButtonFlexContainer>

            <StaticTaskLocationModal
              isOpen={staticLocationModalOpen}
              toggle={() => {
                setStaticLocationModalOpen(false);
                setSelectedStaticLocation(null);
              }}
              taskLocation={selectedStaticLocation}
              locationIndex={locationIndex}
            />
          </AddClientContainerNew>
        </DataAndMapContainer>

        {/* Location Services  */}
        {clientLocationList && clientLocationList.length > 0 ? (
          <GeneralInfoSection2>
            {clientLocationList.map((item, index) => (
              <div
                key={index}
                onMouseEnter={() => onHoverChange(index)}
                onMouseLeave={() => onHoverChange(null)}
                style={{
                  position: "relative",
                  zIndex: hoveredId === index ? 1 : "auto",
                }}
              >
                <CommonText $label>
                  {t("service_location")} {index + 1}{" "}
                  {item.default ? `(${t("default")})` : ""}
                </CommonText>
                {item.default && (
                  <ManagerBadge
                    src={goldenStarBadge}
                    alt="default-location"
                    width="30px"
                    top="29px"
                    left="5px"
                  />
                )}

                {hoveredId !== null && hoveredId === index ? (
                  <HoveredSectionClient>
                    <DeleteButton
                      onClick={() => handleClientLocationDelete(index)}
                    >
                      <CommonImage src={deleteWhiteIcon} alt="" size="16px" />
                    </DeleteButton>
                  </HoveredSectionClient>
                ) : null}
                <div
                  style={{
                    paddingBottom: "10px",
                    paddingTop: "10px",
                    cursor: "pointer",
                  }}
                >
                  <GeneralInfoItem
                    minHeight="130px"
                    backColor={(index + 3) % 8}
                    onClick={() => handleShowLocation(item, index + 1)}
                  >
                    <CommonText title>{item.address}</CommonText>
                    <CommonText $label fontWeight="500">
                      {item.name}
                    </CommonText>
                  </GeneralInfoItem>
                </div>
              </div>
            ))}
          </GeneralInfoSection2>
        ) : (
          <>
            {/* <CommonText $label>{t("service_location")} 1</CommonText>
            <GeneralInfoSection2>
              <GeneralInfoItem>
                <CommonText title>{t("no_address_found")}</CommonText>
                <CommonText $label fontWeight="500">
                  {t("no_name_set")}
                </CommonText>
              </GeneralInfoItem>
            </GeneralInfoSection2> */}
          </>
        )}

        <CommonFlex gap="15px" margin="20px 0 0 0">
          <PrimaryButton
            width="300px"
            disabled={duplicateEmail}
            padding={props.isLoading ? "0 15px" : "0 35px"}
            onClick={!props.isLoading ? () => handleSave() : ""}
            borderRadius="32px"
          >
            {props.isLoading ? (
              <ButtonTextLoader loadingText="Saving" fontSize="14px" />
            ) : (
              t("add_customer")
            )}
          </PrimaryButton>
        </CommonFlex>
        <MapAndAlertContainer></MapAndAlertContainer>
      </Container>
    </div>
  );
};

export default AddClient;
